'use client';

import { useEffect, useRef, useState } from 'react';

import LightGallery from 'lightgallery/react';

import videojs from 'video.js';

import { AfterSlideDetail, BeforeSlideDetail, InitDetail } from 'lightgallery/lg-events';
import type { LightGallery as LightGalleryType } from 'lightgallery/lightgallery';

import { Media } from '@/types/cit-public-api';

// Plugins
import lgVideo from 'lightgallery/plugins/video';
import lgZoom from 'lightgallery/plugins/zoom';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgFullscreen from 'lightgallery/plugins/fullscreen';

import {
  getHighResDerivative,
  isImage,
  isPDF,
  getOriginalPDFUrl,
  isVideo,
  getThumbnailPath,
  getDerivativeVideoPath,
  isAudio,
  getDerivativeAudioPath,
  getPosterPath,
  getDownloadUrl
} from '@/lib/media-utils';

import { useCitOriginClient } from '@/app/CitOriginClientProvider';
import { useQuery } from '@tanstack/react-query';
import { queryFunctions } from '@/lib/query-functions';

declare global {
  interface Window {
    videojs?: typeof videojs;
  }
}

export default function MediaViewer({
  thumbnails,
  enableGallery = true,
  accountId
}: {
  thumbnails: Media[];
  enableGallery?: boolean;
  accountId: string;
}) {
  const galleryRef = useRef<LightGalleryType | null>(null);

  // lightgallery expects a videojs object to be available in the window
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.videojs) {
      window.videojs = videojs;
    }
  }, []);

  // used to track the current slide index, is ref because we don't want to trigger re-renders
  const currentSlideIndex = useRef<number>(0);

  // used to track the index of the slide that is currently being displayed in the main image, is state because we want to trigger re-renders
  const [displayIndex, setDisplayIndex] = useState(0);

  const citOrigin = useCitOriginClient();

  const { data: accountDetails, isLoading: isLoadingAccountDetails } = useQuery({
    queryKey: ['account-details', { accountId, citOrigin }],
    queryFn: queryFunctions.getAccountDetails
  });

  if (isLoadingAccountDetails || !accountDetails) {
    // TODO: need appropriate loading component that fits in here
    return null;
  }

  // controls if audio/video slides are enabled for the account
  const playBack = accountDetails.customization?.features?.playback || false;

  // controls if download button is enabled for the account
  const download = accountDetails.customization?.features?.download || true;

  const handleClickMainImage = () => {
    if (galleryRef.current) {
      galleryRef.current.openGallery(displayIndex);
    }
  };

  const handleSlideChange = (detail: AfterSlideDetail) => {
    currentSlideIndex.current = detail.index;
  };

  const handleClose = () => {
    setDisplayIndex(currentSlideIndex.current);
  };

  const handleInit = (ref: InitDetail) => {
    galleryRef.current = ref.instance;
  };

  const handleClickThumbnail = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDisplayIndex(Number(event.currentTarget.dataset.index));
  };

  // slides with thumbnails and full size images which are displayed inside of the lightgallery when opened
  const slides = thumbnails.map((thumbnail) => {
    if (isPDF(thumbnail)) {
      return (
        // data-iframe='true' is used to open the PDF in an iframe
        <a
          data-download-url={getDownloadUrl(thumbnail)}
          data-iframe='true'
          data-iframe-title={thumbnail.name}
          key={thumbnail.uuid}
          href={getOriginalPDFUrl(thumbnail)}
          data-sub-html={
            thumbnail.name || thumbnail.description
              ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
              : ''
          }
        >
          <img
            alt={
              thumbnail.name && thumbnail.description
                ? `${thumbnail.name}: ${thumbnail.description}`
                : thumbnail.name || thumbnail.description || ''
            }
            src={getThumbnailPath(thumbnail)}
          />
        </a>
      );
    } else if (isImage(thumbnail)) {
      return (
        <a
          data-download-url={getDownloadUrl(thumbnail)}
          key={thumbnail.uuid}
          data-sub-html={
            thumbnail.name || thumbnail.description
              ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
              : ''
          }
          href={getHighResDerivative(thumbnail).path}
        >
          <img
            alt={
              thumbnail.name && thumbnail.description
                ? `${thumbnail.name}: ${thumbnail.description}`
                : thumbnail.name || thumbnail.description || ''
            }
            src={getThumbnailPath(thumbnail)}
          />
        </a>
      );
    } else if (isVideo(thumbnail)) {
      if (playBack) {
        return (
          <a
            // type will always be application/x-mpegURL for videos
            data-video={JSON.stringify({
              source: [{ src: getDerivativeVideoPath(thumbnail), type: 'application/x-mpegURL' }]
            })}
            data-download-url={getDownloadUrl(thumbnail)}
            data-poster={getPosterPath(thumbnail)}
            data-sub-html={
              thumbnail.name || thumbnail.description
                ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                : ''
            }
            key={thumbnail.uuid}
          >
            <img
              src={getThumbnailPath(thumbnail)}
              alt={
                thumbnail.name && thumbnail.description
                  ? `${thumbnail.name}: ${thumbnail.description}`
                  : thumbnail.name || thumbnail.description || ''
              }
            />
          </a>
        );
      } else {
        // display poster image if playback is disabled
        return (
          <a
            data-download-url={getDownloadUrl(thumbnail)}
            key={thumbnail.uuid}
            href={getPosterPath(thumbnail)}
            data-sub-html={
              '<h1>(Video playback is disabled for this account, please contact your administrator)</h1>' +
              (thumbnail.name || thumbnail.description
                ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                : '')
            }
          >
            <img
              alt={
                thumbnail.name && thumbnail.description
                  ? `${thumbnail.name}: ${thumbnail.description}`
                  : thumbnail.name || thumbnail.description || ''
              }
              src={getThumbnailPath(thumbnail)}
            />
          </a>
        );
      }
    } else if (isAudio(thumbnail)) {
      if (playBack) {
        return (
          <a
            // type will always be audio/mp3 for audio
            data-video={JSON.stringify({
              source: [{ src: getDerivativeAudioPath(thumbnail), type: 'audio/mp3' }]
            })}
            data-poster={getPosterPath(thumbnail)}
            data-download-url={getDownloadUrl(thumbnail)}
            data-sub-html={
              thumbnail.name || thumbnail.description
                ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                : ''
            }
            key={thumbnail.uuid}
          >
            <img
              src={getThumbnailPath(thumbnail)}
              alt={
                thumbnail.name && thumbnail.description
                  ? `${thumbnail.name}: ${thumbnail.description}`
                  : thumbnail.name || thumbnail.description || ''
              }
            />
          </a>
        );
      } else {
        return (
          // display poster image if playback is disabled
          <a
            data-download-url={getDownloadUrl(thumbnail)}
            key={thumbnail.uuid}
            href={getPosterPath(thumbnail)}
            data-sub-html={
              '<h1>(Audio playback is disabled for this account, please contact your administrator)</h1>' +
              (thumbnail.name || thumbnail.description
                ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
                : '')
            }
          >
            <img
              alt={
                thumbnail.name && thumbnail.description
                  ? `${thumbnail.name}: ${thumbnail.description}`
                  : thumbnail.name || thumbnail.description || ''
              }
              src={getThumbnailPath(thumbnail)}
            />
          </a>
        );
      }
    } else {
      // will return a placeholder image for unknown media types
      return (
        <a
          data-download-url={getDownloadUrl(thumbnail)}
          key={thumbnail.uuid}
          data-sub-html={
            thumbnail.name || thumbnail.description
              ? `${thumbnail.name ? `<h1>${thumbnail.name}</h1>` : ''}
                 ${thumbnail.description ? `<p>${thumbnail.description}</p>` : ''}`
              : ''
          }
          href={getHighResDerivative(thumbnail).path}
        >
          <img
            alt={
              thumbnail.name && thumbnail.description
                ? `${thumbnail.name}: ${thumbnail.description}`
                : thumbnail.name || thumbnail.description || ''
            }
            src={getThumbnailPath(thumbnail)}
          />
        </a>
      );
    }
  });

  const handleHideButtons = (detail: BeforeSlideDetail) => {
    const slide = slides[detail.index].props;

    if (!slide) {
      return;
    }

    // have to use querySelector as lightGallery has no way of hiding buttons from slide to slide without re-rendering the whole gallery
    const zoomBtn = document.querySelector('.lg-zoom') || document.querySelector('.lg-zoom-in');

    const downloadBtn = document.querySelector('.lg-download');

    if (!zoomBtn || !downloadBtn) {
      return;
    }

    // pdf and audio/video slides should not have a zoom button
    if (slide['data-iframe'] || slide['data-video']) {
      zoomBtn.classList.add('hidden');
    } else {
      zoomBtn.classList.remove('hidden');
    }

    // pdf slides should not have a download button
    if (slide['data-iframe']) {
      downloadBtn.classList.add('hidden');
    } else {
      downloadBtn.classList.remove('hidden');
    }
  };

  return (
    <>
      <div className='h-full w-full max-w-full max-h-full flex flex-col justify-between overflow-hidden gap-2'>
        <div
          onClick={handleClickMainImage}
          className='w-full h-[400px] lg:h-full cursor-pointer overflow-hidden relative'
        >
          {(isVideo(thumbnails[displayIndex]) && playBack) ||
          (isAudio(thumbnails[displayIndex]) && playBack) ? (
            <div className='absolute inset-0 flex items-center justify-center z-10'>
              <div className='w-16 h-16 bg-black/50 rounded-full flex items-center justify-center'>
                <div className='w-0 h-0 border-t-[12px] border-t-transparent border-l-[20px] border-l-white border-b-[12px] border-b-transparent translate-x-[2px]' />
              </div>
            </div>
          ) : (isVideo(thumbnails[displayIndex]) || isAudio(thumbnails[displayIndex])) &&
            !playBack ? (
            <div className='absolute inset-0 flex items-center justify-center z-10'>
              <div className='w-16 h-16 bg-black/50 rounded-full flex items-center justify-center relative'>
                <div className='w-0 h-0 border-t-[12px] border-t-transparent border-l-[20px] border-l-white border-b-[12px] border-b-transparent translate-x-[2px] opacity-50' />
                <div className='absolute inset-0 flex items-center justify-center'>
                  <div className='w-20 h-1 bg-white rotate-45 transform origin-center'></div>
                </div>
              </div>
            </div>
          ) : null}
          <img
            className='w-full h-full object-contain'
            // display poster image for videos/audio
            src={
              isVideo(thumbnails[displayIndex]) || isAudio(thumbnails[displayIndex])
                ? getPosterPath(thumbnails[displayIndex])
                : getHighResDerivative(thumbnails[displayIndex]).path
            }
            alt={
              thumbnails[displayIndex].name && thumbnails[displayIndex].description
                ? `${thumbnails[displayIndex].name}: ${thumbnails[displayIndex].description}`
                : thumbnails[displayIndex].name || thumbnails[displayIndex].description || ''
            }
          />
        </div>

        {thumbnails[displayIndex].name && thumbnails[displayIndex].description ? (
          <div className='w-full justify-center items-center flex flex-col gap-2'>
            <h5 className='text-center text-xl font-bold line-clamp-1'>
              {thumbnails[displayIndex].name}
            </h5>
            <p className='text-center text-sm line-clamp-2'>
              {thumbnails[displayIndex].description}
            </p>
          </div>
        ) : thumbnails[displayIndex].name || thumbnails[displayIndex].description ? (
          <div className='w-full justify-center items-center flex flex-col gap-2'>
            <p
              className={`text-center line-clamp-2 ${
                thumbnails[displayIndex].name ? 'font-bold text-xl' : 'text-sm'
              }`}
            >
              {thumbnails[displayIndex].name || thumbnails[displayIndex].description}
            </p>
          </div>
        ) : null}

        <div
          className='flex overflow-x-auto gap-1 h-[125px] items-center z-100'
          // prevents the slide from being dragged when user interacts with the thumbnails
          data-scrollable='true'
        >
          {/* thumbnails which are actually displayed */}
          {thumbnails.map((thumbnail, index) => {
            return (
              <button
                onClick={handleClickThumbnail}
                className={`h-[100px] w-[100px] flex-shrink-0 rounded-lg border-2 transition-all ${
                  displayIndex === index ? 'border-blue-500' : 'border-transparent'
                }`}
                data-index={index}
                key={thumbnail.uuid}
              >
                <img
                  className='w-full h-full object-cover aspect-square'
                  src={getThumbnailPath(thumbnail)}
                  alt={
                    thumbnail.name && thumbnail.description
                      ? `${thumbnail.name}: ${thumbnail.description}`
                      : thumbnail.name || thumbnail.description || ''
                  }
                />
              </button>
            );
          })}
          {/* only one lightgallery component can be loaded at a time due to videojs needing to have only one instance present */}
          {enableGallery ? (
            <LightGallery
              licenseKey={process.env.NEXT_PUBLIC_LIGHT_GALLERY_LICENSE_KEY || ''}
              onInit={handleInit}
              onAfterSlide={handleSlideChange}
              onBeforeSlide={handleHideButtons}
              onBeforeClose={handleClose}
              speed={500}
              plugins={[lgVideo, lgZoom, lgThumbnail, lgFullscreen]}
              videojs={true}
              videojsOptions={{
                controls: true,
                autoplay: false
              }}
              mobileSettings={{
                showCloseIcon: true
              }}
              // hide internal lightbox thumbnails
              elementClassNames='hidden'
              mode='lg-fade'
              closable={true}
              // controls if download button is shown for the entire gallery
              // overrides the data-download-url attribute on the slides
              download={download}
            >
              {slides}
            </LightGallery>
          ) : null}
        </div>
      </div>
    </>
  );
}
